import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  Icon,
  Divider,
  Spinner,
  Badge,
  useDisclosure,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FiX } from "react-icons/fi";
import { Flame } from "lucide-react";
import {
  getTodayChallengeRequest,
  completeTodayChallengeRequest,
  getPreviousChallengesRequest,
  getExamCorrectionRequest,
  clearDailyChallengeState,
} from "../../modules/students-modules/exams/Actions";
import DailyChallengeContent from "../../containers/student/exams/daily-challenge-content";
import ExamSolvedQuestions from "../../containers/student/exams/exam-solved-questions";
import { getUserDataRequest } from "../../modules/user/Actions";

const DailyChallengeMenu = ({ isOpen, onClose, userData }) => {
  const dispatch = useDispatch();
  const [selectedQuestionIdx, setSelectedQuestionIdx] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [startTime, setStartTime] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [showCorrection, setShowCorrection] = useState(false);
  const [viewingPreviousExamId, setViewingPreviousExamId] = useState(null);

  const todayChallengeQuestions = useSelector(
    (state) => state.chapterExams.todayChallengeQuestions
  );
  const todayChallengeExamId = useSelector(
    (state) => state.chapterExams.todayChallengeExamId
  );
  const challengeCorrection = useSelector(
    (state) => state.chapterExams.challengeCorrection
  );
  const previousChallenges = useSelector(
    (state) => state.chapterExams.previousChallenges
  );
  const examCorrection = useSelector(
    (state) => state.chapterExams.examCorrection
  );
  const isLoading = useSelector((state) => state.chapterExams.isLoading);

  // Check if user can start challenge (24 hours check)
  const canStartChallenge = () => {
    if (!userData?.streak_updated_at) return true;
    const lastUpdate = new Date(userData.streak_updated_at);
    const now = new Date();
    const hoursDiff = (now - lastUpdate) / (1000 * 60 * 60);
    return hoursDiff >= 24;
  };

  // Timer effect
  useEffect(() => {
    if (startTime && !showCorrection) {
      const interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        setElapsedSeconds(elapsed);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [startTime, showCorrection]);

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const canSubmit = elapsedSeconds >= 60; // Minimum 1 minute

  const handleStartChallenge = () => {
    dispatch(getTodayChallengeRequest());
    setStartTime(Date.now());
    setElapsedSeconds(0);
    setShowCorrection(false);
    setAnswers({});
    setSelectedQuestionIdx(0);
    setViewingPreviousExamId(null);
  };

  const handleSubmit = () => {
    if (!canSubmit) {
      return;
    }

    let formData = new FormData();
    formData.append("exam_id", todayChallengeExamId);

    // Convert answers object to array format
    // answers is an object where keys are question indices
    const answersArray = Object.keys(answers)
      .map(key => answers[key])
      .filter(Boolean)
      .filter(data => data.question_id && data.answer_id);

    answersArray.forEach((data, index) => {
      formData.append(`answers[${index}][question_id]`, data.question_id);
      formData.append(`answers[${index}][answer_id]`, data.answer_id);
    });

    const action = () => {
      setShowCorrection(true);
      // Refresh user data to update streak count
      dispatch(getUserDataRequest());
    };

    dispatch(completeTodayChallengeRequest(formData, action));
  };

  const handleViewPreviousCorrection = (examId) => {
    setViewingPreviousExamId(examId);
    dispatch(getExamCorrectionRequest(examId));
    setShowCorrection(true);
  };

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Reset all local state when modal closes
      setShowCorrection(false);
      setViewingPreviousExamId(null);
      setStartTime(null);
      setAnswers({});
      setSelectedQuestionIdx(0);
      setElapsedSeconds(0);
      // Clear Redux state for daily challenge
      dispatch(clearDailyChallengeState());
    } else {
      // Load previous challenges when modal opens
      if (previousChallenges.length === 0) {
        dispatch(getPreviousChallengesRequest());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const isChallengeActive = todayChallengeQuestions.length > 0 && !showCorrection;
  const correctionData = viewingPreviousExamId ? examCorrection : challengeCorrection;

  return (
    <>
      {/* Backdrop */}
      <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="blackAlpha.600"
        zIndex={1000}
        onClick={() => {
          dispatch(clearDailyChallengeState());
          onClose();
        }}
      />

      {/* Menu */}
      <Box
        position="fixed"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        bg="white"
        borderRadius="16px"
        boxShadow="0 10px 40px rgba(0,0,0,0.2)"
        w={{ base: "95%", md: "600px", lg: "700px" }}
        maxH="90vh"
        zIndex={1001}
        overflow="hidden"
        display="flex"
        flexDirection="column"
      >
        {/* Header */}
        <Box
          bg="linear-gradient(135deg, #FF6B35 0%, #F7931E 100%)"
          p={4}
          color="white"
          position="relative"
        >
          <HStack justify="space-between" align="center">
            <HStack spacing={3}>
              <Icon as={Flame} boxSize={6} />
              <VStack align="start" spacing={0}>
                <Text fontWeight="bold" fontSize="lg">
                  Daily Challenge
                </Text>
                <Text fontSize="sm" opacity={0.9}>
                  Streak: {userData?.streak_count || 0} days
                </Text>
              </VStack>
            </HStack>
            <Button
              variant="ghost"
              color="white"
              _hover={{ bg: "whiteAlpha.200" }}
              onClick={() => {
                // Clear state and close
                dispatch(clearDailyChallengeState());
                onClose();
              }}
              size="sm"
            >
              <Icon as={FiX} boxSize={5} />
            </Button>
          </HStack>
        </Box>

        {/* Content */}
        <Box flex={1} overflowY="auto" p={4}>
          {isLoading && !isChallengeActive && !showCorrection ? (
            <VStack spacing={4} py={8}>
              <Spinner size="xl" color="orange.500" />
              <Text color="gray.600">Loading...</Text>
            </VStack>
          ) : showCorrection && correctionData ? (
            // Show correction
            <VStack spacing={4} align="stretch">
              <Box
                bg="green.50"
                p={4}
                borderRadius="8px"
                border="2px solid"
                borderColor="green.200"
              >
                <Text fontWeight="bold" color="green.700" fontSize="lg">
                  Challenge Completed! 🔥
                </Text>
                <Text color="green.600" fontSize="sm" mt={1}>
                  Your streak continues!
                </Text>
              </Box>

              {correctionData?.questions && correctionData.questions.length > 0 ? (
                <DailyChallengeContent
                  questions={correctionData.questions}
                  correctionArr={correctionData?.answers || []}
                  selectedQuestionIdx={selectedQuestionIdx}
                  setSelectedQuestionIdx={setSelectedQuestionIdx}
                  showCorrection={true}
                />
              ) : (
                <Text>Loading correction data...</Text>
              )}

              <HStack spacing={2}>
                {(viewingPreviousExamId) && (
                  <Button
                    onClick={() => {
                      setShowCorrection(false);
                      setViewingPreviousExamId(null);
                    }}
                    variant="outline"
                    colorScheme="orange"
                  >
                    Back to List
                  </Button>
                )}
                <Button
                  onClick={onClose}
                  bg="orange.500"
                  color="white"
                  _hover={{ bg: "orange.600" }}
                >
                  Close
                </Button>
              </HStack>
            </VStack>
          ) : isChallengeActive ? (
            // Active challenge
            <VStack spacing={4} align="stretch">
              {/* Timer */}
              <Box
                bg="orange.50"
                p={3}
                borderRadius="8px"
                border="1px solid"
                borderColor="orange.200"
              >
                <HStack justify="space-between">
                  <Text fontWeight="semibold" color="orange.700">
                    Time: {formatTime(elapsedSeconds)}
                  </Text>
                  {!canSubmit && (
                    <Badge colorScheme="orange">
                      Wait {60 - elapsedSeconds}s to submit
                    </Badge>
                  )}
                </HStack>
              </Box>

              <DailyChallengeContent
                questions={todayChallengeQuestions}
                answers={answers}
                setAnswers={setAnswers}
                selectedQuestionIdx={selectedQuestionIdx}
                setSelectedQuestionIdx={setSelectedQuestionIdx}
                showCorrection={false}
              />

              <HStack spacing={2}>
                <Button
                  onClick={handleSubmit}
                  bg={canSubmit ? "green.500" : "gray.400"}
                  color="white"
                  _hover={canSubmit ? { bg: "green.600" } : {}}
                  isDisabled={!canSubmit || Object.keys(answers).length !== todayChallengeQuestions.length}
                  size="lg"
                  flex={1}
                >
                  Submit Challenge
                </Button>
              </HStack>
              {Object.keys(answers).length !== todayChallengeQuestions.length && (
                <Text fontSize="sm" color="orange.600" textAlign="center">
                  Please answer all questions before submitting
                </Text>
              )}
            </VStack>
          ) : (
            // Start screen
            <VStack spacing={4} align="stretch">
              <Box
                bg="blue.50"
                p={4}
                borderRadius="8px"
                border="1px solid"
                borderColor="blue.200"
              >
                <Text fontWeight="semibold" color="blue.700" mb={2}>
                  Complete today's challenge to keep your streak alive!
                </Text>
                <Text fontSize="sm" color="blue.600">
                  Answer the questions correctly to maintain your daily streak.
                  You must spend at least 1 minute on the challenge.
                </Text>
              </Box>

              {!canStartChallenge() && (
                <Box
                  bg="yellow.50"
                  p={3}
                  borderRadius="8px"
                  border="1px solid"
                  borderColor="yellow.200"
                >
                  <Text fontSize="sm" color="yellow.700">
                    ⏰ You've already completed today's challenge. Come back
                    tomorrow!
                  </Text>
                </Box>
              )}

              <Button
                onClick={handleStartChallenge}
                bg="orange.500"
                color="white"
                _hover={{ bg: "orange.600" }}
                size="lg"
                isDisabled={!canStartChallenge()}
                isLoading={isLoading}
              >
                Start Today's Challenge
              </Button>

              <Divider />

              {/* Previous Challenges */}
              <VStack align="stretch" spacing={2}>
                <Text fontWeight="bold" fontSize="md">
                  Previous Challenges
                </Text>
                {previousChallenges.length === 0 ? (
                  <Text fontSize="sm" color="gray.500">
                    No previous challenges yet
                  </Text>
                ) : (
                  previousChallenges.map((challenge) => (
                    <Box
                      key={challenge.id}
                      p={3}
                      bg="gray.50"
                      borderRadius="8px"
                      border="1px solid"
                      borderColor="gray.200"
                    >
                      <HStack justify="space-between">
                        <VStack align="start" spacing={0}>
                          <Text fontWeight="semibold" fontSize="sm">
                            {challenge.name || `Challenge - ${challenge.id}`}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            {challenge.created_at
                              ? new Date(challenge.created_at).toLocaleDateString()
                              : ""}
                          </Text>
                        </VStack>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewPreviousCorrection(challenge.id)}
                        >
                          View
                        </Button>
                      </HStack>
                    </Box>
                  ))
                )}
              </VStack>
            </VStack>
          )}
        </Box>
      </Box>
    </>
  );
};

export default DailyChallengeMenu;

